import { useRef, useState } from "react";
import { CircularProgress, styled, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Form, Formik, type FormikProps } from "formik";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  FixedSizeList as List,
  type ListChildComponentProps,
} from "react-window";
import { FleetConfigFooter } from "src/components/FleetConfigFooter";
import { FleetConfigTableHeader } from "src/components/FleetConfigTableHeader";
import { FleetConfigLoadAlert } from "src/components/fleet/FleetConfigLoadAlert";
import { FleetConfigSelection } from "src/components/fleet/FleetConfigSelection";
import { FormikDirtyTracker } from "src/forms/FormikDirtyTracker";
import { useFleetConfig, useFleetConfigUpdateMutation } from "src/hooks";
import {
  useFilteredAndSortedFleetConfig,
  useFleetTable,
} from "src/hooks/fleetHooks";
import type {
  FleetConfigItem,
  FleetConfig as IFleetConfig,
  PredefinedLEZOption,
} from "src/types";

import { CustomSwitch } from "./Switch";

interface Props {
  className?: string;
  lezId: number | string;
  scenarioId: number | string;
  readOnly: boolean;
  onClose: () => void;
  options: PredefinedLEZOption[];
  restrictionFleetNameKey: string;
}

export function FleetConfigTable(props: Props) {
  const {
    readOnly,
    lezId,
    scenarioId,
    options,
    restrictionFleetNameKey,
    onClose,
  } = props;
  const { t } = useTranslation();
  const formikRef = useRef<FormikProps<IFleetConfig>>(null);

  const [selectedOption, setSelectedOption] = useState<string>(
    restrictionFleetNameKey
  );

  const [loadedRestrictionFleetNameKey, setLoadedRestrictionFleetNameKey] =
    useState<string>(restrictionFleetNameKey);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { orderBy, orderDirection, filters, handleSort, updateFilters } =
    useFleetTable();

  const { data: fleetConfig, isLoading: isLoadingFleetConfig } = useFleetConfig(
    lezId,
    loadedRestrictionFleetNameKey
  );

  const {
    isLoading,
    mutateAsync: updateFleetConfig,
    error,
  } = useFleetConfigUpdateMutation(
    lezId,
    scenarioId,
    loadedRestrictionFleetNameKey
  );

  const filteredData = useFilteredAndSortedFleetConfig(
    fleetConfig || [],
    filters,
    orderBy,
    orderDirection
  );

  function fleetConfigItemSwitchLabel(item: FleetConfigItem) {
    if (readOnly) return t<string>("label.fleetConfigItemReadOnly");
    if (item.disabled) return t<string>("label.fleetConfigItemDisabled");
    else return "";
  }

  async function handleSave(values: IFleetConfig) {
    await updateFleetConfig(values);
    onClose();
  }

  const pendingFleetLoad =
    selectedOption !== "" && selectedOption !== loadedRestrictionFleetNameKey;

  return (
    <>
      <FleetConfigSelection<IFleetConfig>
        formikRef={formikRef.current}
        options={options}
        readOnly={readOnly}
        selectedOption={selectedOption}
        onSelect={setSelectedOption}
        onLoad={setLoadedRestrictionFleetNameKey}
        loadedOption={loadedRestrictionFleetNameKey}
        isLoading={isLoadingFleetConfig}
        isFormDirty={isFormDirty}
      />
      {pendingFleetLoad && (
        <FleetConfigLoadAlert
          options={options}
          selectedOption={selectedOption}
          loadedOption={loadedRestrictionFleetNameKey}
          readOnly={readOnly}
          isLoading={isLoadingFleetConfig}
          onLoad={() => setLoadedRestrictionFleetNameKey(selectedOption)}
        />
      )}
      {isLoadingFleetConfig && <CircularProgress />}
      {!isLoadingFleetConfig && !pendingFleetLoad && (
        <Formik<IFleetConfig>
          initialValues={fleetConfig || ([] as IFleetConfig)}
          onSubmit={handleSave}
          validateOnChange={false}
          validateOnBlur={false}
          innerRef={formikRef}
          key={loadedRestrictionFleetNameKey}
          enableReinitialize={false}
        >
          {({ values, setFieldValue, isSubmitting, submitForm, dirty }) => (
            <Styles>
              <FormikDirtyTracker
                dirty={dirty}
                onDirtyChange={setIsFormDirty}
              />
              <div className="container">
                <FleetConfigTableHeader
                  orderBy={orderBy}
                  filteredData={filteredData}
                  orderDirection={orderDirection}
                  filters={filters}
                  fleetConfig={fleetConfig}
                  readOnly={readOnly}
                  handleSort={handleSort}
                  updateFilters={updateFilters}
                />

                <div className="list-container">
                  <AutoSizer>
                    {/* @ts-ignore */}
                    {({ height, width }) => (
                      // @ts-expect-error
                      <List
                        height={height}
                        width={width}
                        itemCount={filteredData.length}
                        itemSize={60}
                        overscanCount={5}
                        className="list"
                      >
                        {({ index, style }: ListChildComponentProps) => {
                          const item = filteredData[index];
                          const idx = (fleetConfig || []).findIndex(
                            (r) => r.id === item.id
                          );
                          return (
                            <div
                              className={`row ${index % 2 === 0 ? "even" : "odd"}`}
                              style={style}
                              key={item.id}
                            >
                              <Typography>
                                {item.categoryTranslation}
                              </Typography>
                              <Typography>{item.euroTranslation}</Typography>
                              <Typography>{item.fuelTranslation}</Typography>
                              <Tooltip
                                title={fleetConfigItemSwitchLabel(item)}
                                placement="left"
                              >
                                <span className="align-right">
                                  <CustomSwitch
                                    readOnly={readOnly}
                                    disabled={item.disabled || isSubmitting}
                                    checked={values[idx]?.allowed}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `${idx}.allowed`,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </span>
                              </Tooltip>
                            </div>
                          );
                        }}
                      </List>
                    )}
                  </AutoSizer>
                </div>
              </div>
              <FleetConfigFooter
                onClose={onClose}
                onSave={() => {
                  submitForm();
                }}
                disabled={readOnly || isSubmitting}
                errors={error?.response?.data}
                isLoading={isLoading}
              />
            </Styles>
          )}
        </Formik>
      )}
    </>
  );
}

const Styles = styled(Form)`
  display: flex;
  flex-direction: column;
  height: 100%;
  .predefined-fleet-controls {
    align-items: center;
    display: flex;
    gap: 12px;
    margin-bottom: 12px;
  }
  .container {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    margin-bottom: 20px;
    box-shadow: ${(props) => props.theme.shadows[1]};
  }
  .list-container {
    flex: 1 1 auto;
    min-height: 0;
  }
  .MuiTypography-root {
    font-size: 13px;
  }

  .auto-sizer {
    height: 100% !important;
    width: 100% !important;
  }



  .row {
    height: 60px;
    border-bottom: 1px solid #e0e0e0;
    display: grid;
    padding: 0 24px;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-rows: 1fr;
    align-items: center;
    width: 100%;
    &.odd {
      background-color: #f7f7f7;
    }

    .align-right {
      margin-left: auto;
    }
  }
`;
